import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { getPreparedTransmittals, getResourceState } from '@completion/selectors';
import { BehaviorSubject, Subject, interval, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ResourceStatus, ResourceType, TransmittalSendingState } from '@completion/enums';
import { SendTransmittal } from '@completion/actions';
import { UserChecksheetAssignment } from '../../shared/models/user-checksheet-assignment';

@Component({
  selector: 'app-completed',
  templateUrl: './completed.component.html',
  styleUrls: ['./completed.component.scss'],
  standalone: false,
  exportAs: 'completed',
})
export class CompletedComponent implements OnDestroy {
  @Output() resetState: EventEmitter<void> = new EventEmitter<void>();
  @Output() cancelSend: EventEmitter<void> = new EventEmitter<void>();
  @Output() sent: EventEmitter<void> = new EventEmitter<void>();
  status: ResourceStatus = ResourceStatus.Idle;
  message: BehaviorSubject<string> = new BehaviorSubject<string>('Awaiting start of the transmittal process...');
  statusMessage: BehaviorSubject<string> = new BehaviorSubject<string>('Awaiting start of the transmittal process...');
  transmittalStatus$: BehaviorSubject<TransmittalSendingState> = new BehaviorSubject(TransmittalSendingState.IDLE);
  TransmittalSendingState = TransmittalSendingState; // expose enum for template
  countDown: number = 10;

  private countdownSub: Subscription | null = null;
  private readonly destroy$ = new Subject<boolean>();
  constructor(private store: Store) {
    this.store
      .select(getPreparedTransmittals)
      .pipe(takeUntil(this.destroy$))
      .subscribe((preparedTransmittals) => {
        if (preparedTransmittals && preparedTransmittals.length > 0) {
          this.transmittalStatus$.next(TransmittalSendingState.PREPARED);
        }
      });

    this.transmittalStatus$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      switch (status) {
        case TransmittalSendingState.IDLE:
          this.statusMessage.next('Awaiting start of the transmittal process...');
          this.stopCountdown();
          break;
        case TransmittalSendingState.PREPARED:
          this.statusMessage.next('Transmittal process prepared successfully!');
          // Move to countdown shortly
          setTimeout(() => {
            this.transmittalStatus$.next(TransmittalSendingState.COUNTDOWN);
          }, 300);
          break;
        case TransmittalSendingState.COUNTDOWN:
          this.statusMessage.next(`Transmittal process will start in ${this.countDown} seconds...`);
          this.startCountdown();
          break;
        case TransmittalSendingState.SENDING:
          this.statusMessage.next('Transmittal sending is in progress... Please wait.');
          this.stopCountdown();
          break;
        case TransmittalSendingState.SENT:
          this.statusMessage.next('Transmittal process completed successfully!');
          this.stopCountdown();
          this.sent.emit();
          break;
        case TransmittalSendingState.CANCELLED:
          this.statusMessage.next('Transmittal process cancelled.');
          this.stopCountdown();
          break;
        case TransmittalSendingState.ERROR:
          this.statusMessage.next('Transmittal process failed. Please try again later.');
          this.stopCountdown();
          break;
      }
    });

    this.store
      .select(getResourceState, ResourceType.SendTransmittal)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state) {
          switch (state.status) {
            case ResourceStatus.Idle:
              this.message.next('Awaiting start of the transmittal process...');
              break;
            case ResourceStatus.InProgress:
              this.message.next('Sending transmittal. Awaiting response... Please wait.');
              break;
            case ResourceStatus.Success:
              this.message.next('Transmittal process completed successfully!');
              this.status = ResourceStatus.Success;
              this.transmittalStatus$.next(TransmittalSendingState.SENT);
              // emission handled in status subscription
              break;
            case ResourceStatus.Failure:
              this.message.next(
                'Transmittal process failed. Please try again later. \n Error message: ' +
                  (state.lastError ? state.lastError : 'No error message provided.'),
              );
              this.status = ResourceStatus.Failure;
              this.transmittalStatus$.next(TransmittalSendingState.ERROR);
              break;
            default:
              console.warn('Unknown resource status:', state.status);
              this.message.next('Unknown status. Please check the system.');
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.stopCountdown();
  }

  reset() {
    this.resetState.emit();
  }

  // Countdown helpers
  private startCountdown(): void {
    if (this.countdownSub) {
      return;
    }
    this.countDown = 10;
    this.countdownSub = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.countDown--;
        this.statusMessage.next(`Transmittal process will start in ${this.countDown} seconds...`);
        if (this.countDown <= 0) {
          this.stopCountdown();
          this.transmittalStatus$.next(TransmittalSendingState.SENDING);
          this.dispatchPreparedTransmittals();
        }
      });
  }

  private stopCountdown(): void {
    if (this.countdownSub) {
      this.countdownSub.unsubscribe();
      this.countdownSub = null;
    }
  }

  cancelCountdown(): void {
    if (this.transmittalStatus$.getValue() === TransmittalSendingState.COUNTDOWN) {
      this.stopCountdown();
      this.transmittalStatus$.next(TransmittalSendingState.CANCELLED);
      this.cancelSend.emit();
    }
  }

  sendNow(): void {
    if (this.transmittalStatus$.getValue() === TransmittalSendingState.COUNTDOWN) {
      this.stopCountdown();
      this.transmittalStatus$.next(TransmittalSendingState.SENDING);
      this.dispatchPreparedTransmittals();
    }
  }

  private dispatchPreparedTransmittals(): void {
    this.store
      .select(getPreparedTransmittals)
      .pipe(take(1))
      .subscribe((prepared: UserChecksheetAssignment[] | undefined) => {
        const payload = prepared && prepared.length > 0 ? prepared : [];
        if (payload.length === 0) {
          this.message.next('Nothing to send (no prepared transmittals).');
          this.transmittalStatus$.next(TransmittalSendingState.ERROR);
          return;
        }
        this.store.dispatch(new SendTransmittal(payload));
      });
  }
}
